import React, { useState, useEffect } from 'react';
import adminService from '../services/adminServices';
import '../styles/adminManage.css';

const AdminParkingManagement = ({ onClose, refreshUsers }) => {
  const [admins, setAdmins] = useState([]);
  const [availableParkings, setAvailableParkings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [editingParkings, setEditingParkings] = useState([]);

  // Cargar administradores y parqueos disponibles
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const [adminsResponse, parkingsResponse] = await Promise.all([
        adminService.getAllAdmins(),
        adminService.getAvailableParkings()
      ]);

      if (adminsResponse.success) {
        // Filtrar solo administradores regulares (no SuperAdmin)
        const regularAdmins = adminsResponse.admins.filter(admin => admin.adminLevel !== 'SuperAdmin');
        setAdmins(regularAdmins);
      }

      if (parkingsResponse.success) {
        setAvailableParkings(parkingsResponse.parkings);
      }
    } catch (err) {
      setError('Error al cargar datos: ' + (err.response?.data?.message || err.message));
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditParkings = (admin) => {
    setSelectedAdmin(admin);
    // Inicializar con los parqueos actualmente asignados
    setEditingParkings(admin.assignedParkings?.map(p => p._id) || []);
  };

  const handleParkingToggle = (parkingId) => {
    setEditingParkings(prev => {
      if (prev.includes(parkingId)) {
        return prev.filter(id => id !== parkingId);
      } else {
        return [...prev, parkingId];
      }
    });
  };

  const handleSaveChanges = async () => {
   
    if (!selectedAdmin) return;

    try {
      setError(null);
      
      // Asignar los nuevos parqueos seleccionados
      await adminService.assignParkingsToAdmin(selectedAdmin.idCard, editingParkings);
      
      // Recargar datos
      await loadData();
      
      // Cerrar editor
      setSelectedAdmin(null);
      setEditingParkings([]);
      
      // Notificar al componente padre para refrescar si es necesario
      if (refreshUsers) {
        refreshUsers();
      }
      
      alert('Asignación de parqueos actualizada exitosamente');
    } catch (err) {
      setError('Error al actualizar asignación: ' + (err.response?.data?.message || err.message));
      console.error('Error updating parking assignment:', err);
    }
  };

  const handleCancelEdit = () => {
    setSelectedAdmin(null);
    setEditingParkings([]);
  };

  const getParkingName = (parkingId) => {
    const parking = availableParkings.find(p => p._id === parkingId);
    return parking ? parking.parkingName : 'Parqueo no encontrado';
  };

  if (loading) {
    return (
      <div className="apm-overlay">
        <div className="apm-modal">
          <div className="apm-loading">Cargando administradores...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="apm-overlay">
      <div className="apm-modal">
        <div className="apm-header">
          <h2>Gestión de Asignaciones de Parqueo</h2>
          <button className="apm-close-btn" onClick={onClose}>×</button>
        </div>

        {error && <div className="apm-error">{error}</div>}

        <div className="apm-content">
          {!selectedAdmin ? (
            // Lista de administradores
            <div className="apm-admin-list">
              <h3>Administradores Registrados</h3>
              {admins.length === 0 ? (
                <p className="apm-no-admins">No hay administradores registrados</p>
              ) : (
                <div className="apm-admin-grid">
                  {admins.map(admin => (
                    <div key={admin.idCard} className="apm-admin-card">
                      <div className="apm-admin-info">
                        <h4>{admin.idCard.name} {admin.lastname}</h4>
                        <p className="apm-admin-email">{admin.email}</p>
                        <p className="apm-admin-id">Cédula: {admin.idCard}</p>
                       </div>
                      
                      <div className="apm-admin-parkings">
                        <strong>Parqueos asignados ({admin.assignedParkings?.length || 0}):</strong>
                        {admin.assignedParkings?.length > 0 ? (
                          <ul className="apm-parking-list">
                            {admin.assignedParkings.map(parking => (
                              <li key={parking._id}>
                                {parking.parkingName}
                                <span className="apm-parking-capacity">({parking.capacity} espacios)</span>
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <p className="apm-no-parkings">Sin parqueos asignados</p>
                        )}
                      </div>
                      
                      <button 
                        className="apm-edit-btn"
                        onClick={() => handleEditParkings(admin)}
                      >
                        Editar Asignaciones
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            // Editor de asignaciones
            <div className="apm-assignment-editor">
              <div className="apm-editor-header">
                <h3>Editar asignaciones para {selectedAdmin.idCard.name} {selectedAdmin.idCard.lastname}</h3>
                <div className="apm-editor-actions">
                  <button className="apm-cancel-btn" onClick={handleCancelEdit}>
                    Cancelar
                  </button>
                  <button className="apm-save-btn" onClick={handleSaveChanges}>
                    Guardar Cambios
                  </button>
                </div>
              </div>

              <div className="apm-parking-selection">
                <p className="apm-selection-instruction">
                  Seleccione los parqueos que gestionará este administrador:
                </p>
                
                {availableParkings.length === 0 ? (
                  <p className="apm-no-parkings-available">No hay parqueos disponibles</p>
                ) : (
                  <div className="apm-parking-grid">
                    {availableParkings.map(parking => (
                      <div 
                        key={parking._id}
                        className={`apm-parking-option ${editingParkings.includes(parking._id) ? 'selected' : ''}`}
                        onClick={() => handleParkingToggle(parking._id)}
                      >
                        <div className="apm-parking-details">
                          <h4>{parking.parkingName}</h4>
                          <p>Capacidad: {parking.capacity} espacios</p>
                          <p>Tarifa: ₡{parking.tarify} por hora</p>
                        </div>
                        <div className="apm-parking-checkbox">
                          <input 
                            type="checkbox"
                            checked={editingParkings.includes(parking._id)}
                            onChange={() => handleParkingToggle(parking._id)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {editingParkings.length > 0 && (
                  <div className="apm-selected-summary">
                    <strong>Parqueos seleccionados ({editingParkings.length}):</strong>
                    <ul>
                      {editingParkings.map(id => (
                        <li key={id}>{getParkingName(id)}</li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminParkingManagement;